import * as React from 'react';
import { cn } from '../../lib/utils';
import { ZafroiIcon } from './ZafroiIcon';

export interface ZafroiLogoProps extends React.HTMLAttributes<HTMLDivElement> {
  /** `mark` = icon only; `lockup` = icon + wordmark. */
  variant?: 'mark' | 'lockup';
  /** Sit on a light or dark surface. */
  tone?: 'dark' | 'light';
  /** Icon pixel size. The wordmark scales with it. */
  size?: number;
  /** Optional subtitle / product name beneath the wordmark (e.g. "Operations"). */
  subtitle?: string;
}

/**
 * ZAFROI brand lockup. Defaults to the lockup variant (icon + wordmark) for
 * topbars and auth screens. Use `variant="mark"` when the icon alone is
 * enough.
 */
export const ZafroiLogo = React.forwardRef<HTMLDivElement, ZafroiLogoProps>(
  (
    {
      variant = 'lockup',
      tone = 'dark',
      size = 28,
      subtitle,
      className,
      ...props
    },
    ref,
  ) => {
    const wordColor = tone === 'dark' ? 'text-foreground' : 'text-white';
    const subColor =
      tone === 'dark' ? 'text-muted-foreground' : 'text-white/60';

    if (variant === 'mark') {
      return (
        <div
          ref={ref}
          className={cn('inline-flex items-center', className)}
          {...props}
        >
          <ZafroiIcon size={size} tone={tone} />
        </div>
      );
    }

    return (
      <div
        ref={ref}
        className={cn('inline-flex items-center gap-2.5', className)}
        {...props}
      >
        <ZafroiIcon size={size} tone={tone} />
        <div className="flex flex-col leading-none">
          <span
            className={cn(
              'text-[15px] font-semibold tracking-[0.14em]',
              wordColor,
            )}
          >
            ZAFROI
          </span>
          {subtitle ? (
            <span
              className={cn(
                'mt-1 text-[10px] font-medium uppercase tracking-[0.22em]',
                subColor,
              )}
            >
              {subtitle}
            </span>
          ) : null}
        </div>
      </div>
    );
  },
);
ZafroiLogo.displayName = 'ZafroiLogo';
